<template>
  <section class="mt-4" aria-labelledby="property-profile-heading">
    <div id="property-profile-heading" class="form-head"><i class="bi bi-chevron-left"></i> بروفايل العقار:</div>
    <mainLoader v-if="loading" />
    <template v-else>
      <errorAlert v-if="error" :alertMessage="error" />
      <successAlert v-if="success" message="تم حفظ بروفايل العقار بنجاح" />
      <div class="row align-items-end mb-3">
        <div class="col-12 col-sm-6">
          <label for="property-profile-status" class="form-label">حالة بروفايل العقار</label>
          <select id="property-profile-status" v-model="profile.profileStatus" class="form-control" :disabled="busy || !canUpdate || !loaded || completed">
            <option v-for="(label, value) in statusChoices" :key="value" :value="value" :disabled="value === 'completed' && !profile.profileReportPath">{{ label }}</option>
          </select>
        </div>
        <div v-if="canUpdate && !completed" class="col-12 col-sm-6 mt-2">
          <button type="button" class="btn btn-success" :disabled="busy || !loaded" @click="saveStatus">حفظ الحالة</button>
        </div>
      </div>
      <div v-if="profile.profileReportPath" class="d-flex align-items-center gap-3 p-2 border rounded mb-3">
        <i class="bi bi-file-earmark-pdf-fill text-danger fs-3"></i>
        <span class="flex-grow-1">{{ profile.profileReportName || 'بروفايل العقار' }}</span>
        <button type="button" class="btn btn-outline-primary btn-sm" :disabled="busy" @click="download">تحميل</button>
        <button v-if="canUpdate && !completed" type="button" class="btn btn-outline-danger btn-sm" :disabled="busy" @click="remove">حذف</button>
      </div>
      <div v-if="canUpdate && !completed" class="mb-3">
        <label for="property-profile-file" class="form-label">{{ profile.profileReportPath ? 'استبدال بروفايل العقار (PDF)' : 'رفع بروفايل العقار (PDF)' }}</label>
        <input id="property-profile-file" type="file" accept=".pdf,application/pdf" class="form-control" :disabled="busy || !loaded" @change="upload" />
      </div>
      <p v-if="completed" class="text-muted">تم الانتهاء من بروفايل العقار، ولا يمكن تعديل الحالة أو الملف.</p>
      <p v-else class="text-muted">ارفع ملف PDF واحفظه أولاً، ثم اختر «تم الانتهاء» واحفظ الحالة. لا يمكن تغييرها بعد ذلك.</p>
      <loader v-if="busy" />
    </template>
  </section>
</template>

<script>
import { checkAccess } from '@/helpers/tools';

export default {
  name: 'PropertyProfile',
  props: {
    propertyId: { type: [Number, String], default: null },
    containerId: { type: [Number, String], default: null },
  },
  data() {
    return {
      loading: false, loaded: false, busy: false, error: '', success: false,
      statusChoices: {},
      savedStatus: 'not_requested',
      profile: { profileStatus: 'not_requested', profileReportPath: null, profileReportName: null },
    };
  },
  computed: {
    completed() { return this.savedStatus === 'completed'; },
    canUpdate() { return checkAccess(this.containerId ? 'property_containers_update' : 'property_update'); },
    endpoint() { return this.containerId ? `/api/admin/propertyContainers/${this.containerId}/profile` : `/api/admin/properties/${this.propertyId}/additionals`; },
  },
  watch: {
    propertyId: { immediate: true, handler() { if(this.propertyId) this.load(); } },
    containerId: { immediate: true, handler() { if(this.containerId) this.load(); } },
  },
  methods: {
    applyProfile(row) {
      this.savedStatus = row?.profileStatus ?? 'not_requested';
      this.profile = {
        profileStatus: row?.profileStatus ?? 'not_requested',
        profileReportPath: row?.profileReportPath ?? null,
        profileReportName: row?.profileReportName ?? null,
      };
    },
    async load() {
      this.loading = true;
      this.loaded = false;
      this.error = '';
      try {
        const { data } = await this.$http.get(this.endpoint);
        this.statusChoices = data.profileStatusChoices || {};
        this.applyProfile(data.additionals);
        this.loaded = true;
      } catch (err) { this.error = this.$globalError(err); }
      finally { this.loading = false; }
    },
    async update(request) {
      if (this.busy || !this.canUpdate || !this.loaded || this.completed) return;
      this.busy = true;
      this.error = '';
      this.success = false;
      try {
        const { data } = await request();
        this.applyProfile(data.additionals);
        this.success = true;
      } catch (err) { this.error = this.$globalError(err); }
      finally { this.busy = false; }
    },
    saveStatus() {
      if (this.profile.profileStatus === 'completed' && !this.profile.profileReportPath) {
        this.error = 'يجب رفع وحفظ ملف PDF قبل اختيار تم الانتهاء';
        this.success = false;
        return;
      }
      return this.update(() => this.$http.put(this.endpoint, { profileStatus: this.profile.profileStatus }));
    },
    upload(event) {
      const file = event.target.files[0];
      event.target.value = '';
      if (!file) return;
      if (!/\.pdf$/i.test(file.name)) {
        this.error = 'يجب أن يكون الملف بصيغة PDF';
        this.success = false;
        return;
      }
      const data = new FormData();
      data.append('file', file);
      data.append('name', file.name);
      return this.update(() => this.$http.put(`${this.endpoint}/${this.containerId ? 'report' : 'profileReport'}`, data));
    },
    remove() { return this.update(() => this.$http.delete(`${this.endpoint}/${this.containerId ? 'report' : 'profileReport'}`)); },
    async download() {
      if (this.busy) return;
      this.busy = true;
      this.error = '';
      try {
        const { data } = await this.$http.get(`${this.endpoint}/${this.containerId ? 'report' : 'profileReport'}`, { responseType: 'blob' });
        const url = URL.createObjectURL(data);
        const link = document.createElement('a');
        link.href = url;
        link.download = (this.profile.profileReportName || 'property-profile').replace(/\.pdf$/i, '') + '.pdf';
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      } catch (err) { this.error = 'تعذر تحميل بروفايل العقار، يرجى المحاولة مرة أخرى'; }
      finally { this.busy = false; }
    },
  },
};
</script>
